import dayjs from 'dayjs';
import React from 'react';
import styled from 'styled-components';

import { Modal } from '@material-ui/core';
import Popover from '@material-ui/core/Popover';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { icons } from '../../themes/icons';
import ModalBody from './ModalBody';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    typography: {
      padding: theme.spacing(2),
    },
  }),
);

type Props = {
  selectedLabel: string;
  setSelectedLabel: React.Dispatch<React.SetStateAction<string>>;
  fromDate: string | null;
  setFromDate: React.Dispatch<React.SetStateAction<string | null>>;
  toDate: string | null;
  setToDate: React.Dispatch<React.SetStateAction<string | null>>;
  setPriorFrom: React.Dispatch<React.SetStateAction<string | null>>;
  setPriorTo: React.Dispatch<React.SetStateAction<string | null>>;
  includeAllTime?: boolean;
};

const DateDropdown: React.FC<Props> = ({
  selectedLabel,
  setSelectedLabel,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  setPriorFrom,
  setPriorTo,
  includeAllTime = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [modalOpen, setModalOpen] = React.useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  // Using dayjs to dynamically calculate date ranges
  const dateRanges = [
    ...(includeAllTime
      ? [{ label: 'All time', from: null, to: null, priorFrom: null, priorTo: null }]
      : []),
    {
      label: 'Yesterday',
      from: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
      to: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
      priorFrom: dayjs().subtract(2, 'day').format('YYYY-MM-DD'), // Day before yesterday
      priorTo: dayjs().subtract(2, 'day').format('YYYY-MM-DD'),
    },
    {
      label: 'Last 7 days',
      from: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
      to: dayjs().format('YYYY-MM-DD'),
      priorFrom: dayjs().subtract(14, 'day').format('YYYY-MM-DD'), // 14 days ago
      priorTo: dayjs().subtract(8, 'day').format('YYYY-MM-DD'),
    },
    {
      label: 'Last 30 days',
      from: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
      to: dayjs().format('YYYY-MM-DD'),
      priorFrom: dayjs().subtract(60, 'day').format('YYYY-MM-DD'), // 60 days ago
      priorTo: dayjs().subtract(31, 'day').format('YYYY-MM-DD'),
    },
    {
      label: 'Last 90 days',
      from: dayjs().subtract(90, 'day').format('YYYY-MM-DD'),
      to: dayjs().format('YYYY-MM-DD'),
      priorFrom: dayjs().subtract(180, 'day').format('YYYY-MM-DD'), // 180 days ago
      priorTo: dayjs().subtract(91, 'day').format('YYYY-MM-DD'),
    },
    {
      label: 'Year to date',
      from: dayjs().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
      to: dayjs().format('YYYY-MM-DD'),
      priorFrom: dayjs().subtract(2, 'year').startOf('year').format('YYYY-MM-DD'), // 2 years ago
      priorTo: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
    },
    {
      label: 'Last 12 months',
      from: dayjs().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
      to: dayjs().subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
      priorFrom: dayjs().subtract(2, 'year').startOf('year').format('YYYY-MM-DD'), // 2 years ago
      priorTo: dayjs().subtract(2, 'year').endOf('year').format('YYYY-MM-DD'),
    },
    { label: 'Custom', from: null, to: null, priorFrom: null, priorTo: null },
  ];

  const handleGetDates = (item: {
    label: string;
    from: string | null;
    to: string | null;
    priorFrom: string | null;
    priorTo: string | null;
  }) => {
    if (item.label === 'Custom') {
      setModalOpen(true);
      setSelectedLabel(item.label);
      handleClose();
    } else {
      setFromDate(item.from);
      setToDate(item.to);
      setPriorFrom(item.priorFrom);
      setPriorTo(item.priorTo);
      setSelectedLabel(item.label); // Set the label for display
      handleClose();
    }
  };

  return (
    <div>
      <SelectButton aria-describedby={id} onClick={handleClick}>
        {selectedLabel === 'Custom' ? `${fromDate || ''} - ${toDate || ''}` : selectedLabel}
        <icons.DropDownIcon
          className="date-dropdown-icon"
          style={open ? { transform: 'rotate(180deg)' } : {}}
        />
      </SelectButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <DropDownList>
          {dateRanges.map((item, index) => (
            <ListItem key={index} onClick={() => handleGetDates(item)}>
              {item.label === 'Custom' && <icons.CalendarIcon color="#375fda" />}
              <span>{item.label}</span>
            </ListItem>
          ))}
        </DropDownList>
      </Popover>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <ModalBody
          setModalOpen={setModalOpen}
          setFromDate={setFromDate}
          setToDate={setToDate}
          setPriorFrom={setPriorFrom}
          setPriorTo={setPriorTo}
        />
      </Modal>
    </div>
  );
};

export default DateDropdown;

const SelectButton = styled.button`
  background-color: #fff;
  padding: 0 1rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 190px;
  height: 32px;
  gap: 0px;
  border-radius: 6px;
  border: 1px solid #e6e6e6;
  color: #848484;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
`;

const DropDownList = styled.ul`
  list-style: none;
  min-width: 190px;
  padding: 0;
  margin: 0;
`;

const ListItem = styled.li`
  color: #111111;
  font-size: 12px;
  font-family: Poppins;
  font-weight: 400;
  wordwrap: break-word;
  cursor: pointer;
  border-bottom: 1px solid #00000014;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  svg {
    path {
      fill: #375fda;
    }
  }
  &:last-child {
    color: #375fda;
  }
`;
